import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { planPublicPathSchema, type PlanPublic } from '@plans/shared';
import { ApiError, plansApi } from '../api/client.js';
import { CopyButton } from '../components/CopyButton.js';
import { FolderUpload } from '../components/FolderUpload.js';
import { StatusBadge } from '../components/StatusBadge.js';

function passwordMeetsStrength(password: string): string | null {
  if (password.length < 10) {
    return 'يجب أن تتكون كلمة المرور من 10 أحرف على الأقل.';
  }
  if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
    return 'يجب أن تتضمن كلمة المرور حرفًا واحدًا ورقمًا واحدًا على الأقل.';
  }
  return null;
}

function formatDate(value: string | null): string {
  if (!value) return 'لم يتم التحديث';
  return new Date(value).toLocaleString('ar');
}

export function PlanDetailPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanPublic | null>(null);
  const [title, setTitle] = useState('');
  const [publicPath, setPublicPath] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!uuid) return;
    void plansApi
      .get(uuid)
      .then(({ plan: next }) => {
        setPlan(next);
        setTitle(next.title);
        setPublicPath(next.publicPath ?? '');
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'تعذر تحميل هذه الخطة.');
      });
  }, [uuid]);

  if (!uuid) {
    return <p className="error">معرّف الخطة مفقود.</p>;
  }
  if (!plan && !error) {
    return <p className="muted">جارٍ تحميل الخطة…</p>;
  }
  if (!plan) {
    return <p className="error">{error}</p>;
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{plan.title}</h1>
          <p className="muted">أدر النشر وحماية كلمة المرور ومجلد الموقع المرفوع.</p>
        </div>
        <div className="action-group">
          <Link className="btn secondary" to="/">
            رجوع
          </Link>
          <a className="btn" href={plan.publicUrl} target="_blank" rel="noreferrer">
            فتح الخطة
          </a>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}

      <section className="card stack">
        <h2>العنوان</h2>
        <label>
          عنوان الخطة
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} />
        </label>
        <div className="section-actions">
          <button
            type="button"
            className="btn"
            disabled={pending || title.trim().length === 0}
            onClick={() => {
              setPending(true);
              setError(null);
              void plansApi
                .patch(plan.id, { title: title.trim() })
                .then(({ plan: next }) => {
                  setPlan(next);
                  setNotice('تم حفظ العنوان.');
                })
                .catch((err: unknown) => {
                  setError(err instanceof ApiError ? err.message : 'تعذر حفظ العنوان.');
                })
                .finally(() => setPending(false));
            }}
          >
            حفظ العنوان
          </button>
        </div>
      </section>

      <section className="card stack">
        <h2>المسار العام والرابط</h2>
        <div>
          <div className="muted">المعرّف</div>
          <div className="mono" dir="ltr">
            {plan.id}
          </div>
        </div>
        <label>
          مسار عام مخصص
          <input
            type="text"
            dir="ltr"
            maxLength={64}
            autoComplete="off"
            spellCheck={false}
            placeholder={plan.id}
            value={publicPath}
            onChange={(event) => setPublicPath(event.target.value)}
          />
        </label>
        <p className="field-hint muted">
          أحرف إنجليزية صغيرة وأرقام وشرطات. اتركه فارغًا لاستخدام المعرّف.
        </p>
        <div>
          <div className="muted">الرابط العام</div>
          <div className="row">
            <span className="mono" dir="ltr">
              {plan.publicUrl}
            </span>
            <CopyButton value={plan.publicUrl} />
          </div>
        </div>
        <div className="section-actions">
          <button
            type="button"
            className="btn"
            disabled={pending}
            onClick={() => {
              const trimmed = publicPath.trim();
              if (trimmed) {
                const parsed = planPublicPathSchema.safeParse(trimmed);
                if (!parsed.success) {
                  setError(parsed.error.issues[0]?.message ?? 'المسار العام غير صالح.');
                  return;
                }
              }
              setPending(true);
              setError(null);
              void plansApi
                .patch(plan.id, { publicPath: trimmed === '' ? null : trimmed })
                .then(({ plan: next }) => {
                  setPlan(next);
                  setPublicPath(next.publicPath ?? '');
                  setNotice(
                    next.publicPath
                      ? 'تم حفظ المسار. شارك الرابط الجديد؛ ما زال المعرّف يحوّل إليه.'
                      : 'أُعيد المسار العام إلى معرّف الخطة.',
                  );
                })
                .catch((err: unknown) => {
                  setError(err instanceof ApiError ? err.message : 'تعذر حفظ المسار العام.');
                })
                .finally(() => setPending(false));
            }}
          >
            حفظ المسار
          </button>
        </div>
      </section>

      <section className="card stack">
        <h2>النشر</h2>
        <div className="row">
          <StatusBadge on={plan.published} onLabel="منشورة" offLabel="غير منشورة" />
          <StatusBadge on={plan.passwordProtected} onLabel="كلمة المرور مفعّلة" offLabel="بدون كلمة مرور" />
        </div>
        <p className="muted">آخر تحديث للملفات: {formatDate(plan.filesUpdatedAt)}</p>
        <div className="section-actions">
          <button
            type="button"
            className="btn"
            disabled={pending}
            onClick={() => {
              setPending(true);
              setError(null);
              void plansApi
                .patch(plan.id, { published: !plan.published })
                .then(({ plan: next }) => {
                  setPlan(next);
                  setNotice(next.published ? 'تم نشر الخطة.' : 'تم إلغاء نشر الخطة.');
                })
                .catch((err: unknown) => {
                  setError(err instanceof ApiError ? err.message : 'تعذر تحديث النشر.');
                })
                .finally(() => setPending(false));
            }}
          >
            {plan.published ? 'إلغاء النشر' : 'نشر'}
          </button>
        </div>
      </section>

      <section className="card stack">
        <h2>كلمة المرور</h2>
        <p>
          حماية كلمة المرور: <strong>{plan.passwordProtected ? 'مفعّلة' : 'غير مفعّلة'}</strong>
        </p>
        {plan.passwordProtected && (
          <p className="muted">
            لا يمكن عرض كلمة المرور الحالية. تغييرها يقفل الخطة فورًا على الزوار الذين فتحوها من قبل.
          </p>
        )}
        <p className="field-hint muted">10 أحرف على الأقل، مع حرف واحد ورقم واحد.</p>
        <label>
          كلمة المرور الجديدة
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setPasswordError(null);
            }}
          />
        </label>
        <label>
          تأكيد كلمة المرور
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => {
              setConfirm(event.target.value);
              setPasswordError(null);
            }}
          />
        </label>
        {passwordError && <p className="error">{passwordError}</p>}
        <div className="section-actions">
          <button
            type="button"
            className="btn"
            disabled={pending || password.length === 0 || confirm.length === 0}
            onClick={() => {
              const strength = passwordMeetsStrength(password);
              if (strength) {
                setPasswordError(strength);
                return;
              }
              if (password !== confirm) {
                setPasswordError('كلمتا المرور غير متطابقتين.');
                return;
              }
              setPending(true);
              setPasswordError(null);
              setNotice(null);
              void plansApi
                .setPassword(plan.id, password)
                .then(({ plan: next }) => {
                  setPlan(next);
                  setPassword('');
                  setConfirm('');
                  setNotice('تم تحديث حماية كلمة المرور.');
                })
                .catch((err: unknown) => {
                  setPasswordError(err instanceof ApiError ? err.message : 'تعذر تعيين كلمة المرور.');
                })
                .finally(() => setPending(false));
            }}
          >
            {plan.passwordProtected ? 'تغيير كلمة المرور' : 'تعيين كلمة المرور'}
          </button>
          {plan.passwordProtected && (
            <button
              type="button"
              className="btn secondary"
              disabled={pending}
              onClick={() => {
                if (!window.confirm('إزالة حماية كلمة المرور من هذه الخطة؟')) {
                  return;
                }
                setPending(true);
                setPasswordError(null);
                void plansApi
                  .removePassword(plan.id)
                  .then(({ plan: next }) => {
                    setPlan(next);
                    setNotice('تمت إزالة حماية كلمة المرور.');
                  })
                  .catch((err: unknown) => {
                    setPasswordError(err instanceof ApiError ? err.message : 'تعذر إزالة كلمة المرور.');
                  })
                  .finally(() => setPending(false));
              }}
            >
              إزالة كلمة المرور
            </button>
          )}
        </div>
      </section>

      <section className="card stack">
        <h2>الملفات</h2>
        <FolderUpload
          planId={plan.id}
          onUploaded={(next) => {
            setPlan(next);
            setNotice('تم تحديث ملفات الخطة. حدّث الرابط العام لرؤية النسخة الجديدة.');
          }}
        />
      </section>

      <section className="card stack danger-zone">
        <h2>منطقة الخطر</h2>
        <p className="muted">حذف الخطة يزيل الملفات والرابط العام ولا يمكن التراجع عنه.</p>
        <div className="section-actions">
          <button
            type="button"
            className="btn danger"
            onClick={() => {
              if (!window.confirm(`حذف «${plan.title}»؟ لا يمكن التراجع عن هذا الإجراء.`)) {
                return;
              }
              void plansApi
                .remove(plan.id)
                .then(() => navigate('/'))
                .catch((err: unknown) => {
                  setError(err instanceof ApiError ? err.message : 'تعذر حذف هذه الخطة.');
                });
            }}
          >
            حذف الخطة
          </button>
        </div>
      </section>
    </div>
  );
}
